// FILE: src/components/admin/AdminLeavePolicyManager.jsx
// NEW FILE — place in src/components/admin/

import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

const LEAVE_TYPE_OPTIONS = [
  { value: 'CL', label: 'Casual Leave (CL)' },
  { value: 'EL', label: 'Earned Leave (EL)' },
  { value: 'RH', label: 'Restricted Holiday (RH)' },
  { value: 'HPL', label: 'Half Pay Leave (HPL)' },
  { value: 'CCL', label: 'Child Care Leave (CCL)' },
  { value: 'COL', label: 'Compensatory Leave (COL)' },
  { value: 'MEDICAL', label: 'Medical Leave' },
  { value: 'SPECIAL', label: 'Special Leave' },
  { value: 'ONE_THIRD', label: 'One Third' },
  { value: 'OTHERS', label: 'Others' },
];

const AdminLeavePolicyManager = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    leave_type: 'CL',
    year: new Date().getFullYear(),
    allocated_days: '',
    reset_used: false
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getLeavePolicies();
      setPolicies(Array.isArray(data) ? data : (data?.results || []));
    } catch (err) {
      setError('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (policy) => {
    setEditingId(policy.id);
    setEditForm({
      annual_allocation: policy.annual_allocation,
      is_paid: policy.is_paid,
      allow_negative: policy.allow_negative,
      requires_medical_certificate: policy.requires_medical_certificate,
      is_active: policy.is_active
    });
    setMessage('');
    setError('');
  };

  const handleSaveEdit = async (policyId, syncBalances) => {
    setSaving(true);
    setError('');
    try {
      const result = await adminAPI.updateLeavePolicy(policyId, editForm, syncBalances);
      setMessage(
        `Policy updated successfully.${syncBalances ? ` ${result.synced_users} users' balances synced.` : ''}`
      );
      setEditingId(null);
      loadPolicies();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const result = await adminAPI.bulkUpdateLeaveBalances(bulkForm);
      setMessage(
        `Bulk update done! Created: ${result.users_created}, Updated: ${result.users_updated} users.`
      );
      setShowBulkModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading policies…</div>;

  return (
    <div className="admin-leave-policy">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Leave Type Policies</h2>
        <button
          className="btn-primary"
          onClick={() => { setShowBulkModal(true); setMessage(''); setError(''); }}
        >
          Bulk Allocate Days
        </button>
      </div>

      {message && (
        <div className="success-message" style={{ marginBottom: '1rem' }}>{message}</div>
      )}
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Leave Type</th>
              <th>Annual Days</th>
              <th>Paid?</th>
              <th>Allow Negative (LOP)</th>
              <th>Medical Cert?</th>
              <th>Active?</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((policy) => (
              <tr key={policy.id}>
                <td><strong>{policy.leave_type_display}</strong></td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={editForm.annual_allocation}
                      onChange={(e) => setEditForm({ ...editForm, annual_allocation: e.target.value })}
                      style={{ width: '80px', padding: '4px' }}
                    />
                  ) : (
                    <strong style={{ color: '#2563eb' }}>{policy.annual_allocation} days</strong>
                  )}
                </td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="checkbox"
                      checked={editForm.is_paid}
                      onChange={(e) => setEditForm({ ...editForm, is_paid: e.target.checked })}
                    />
                  ) : (
                    <span className={`badge ${policy.is_paid ? 'badge-green' : 'badge-red'}`}>
                      {policy.is_paid ? 'Paid' : 'Unpaid'}
                    </span>
                  )}
                </td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="checkbox"
                      checked={editForm.allow_negative}
                      onChange={(e) => setEditForm({ ...editForm, allow_negative: e.target.checked })}
                    />
                  ) : (
                    <span className={`badge ${policy.allow_negative ? 'badge-orange' : 'badge-gray'}`}>
                      {policy.allow_negative ? 'Allowed' : 'Not allowed'}
                    </span>
                  )}
                </td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="checkbox"
                      checked={editForm.requires_medical_certificate}
                      onChange={(e) => setEditForm({ ...editForm, requires_medical_certificate: e.target.checked })}
                    />
                  ) : (policy.requires_medical_certificate ? '✅' : '—')}
                </td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    />
                  ) : (policy.is_active ? '✅' : '❌')}
                </td>
                <td>
                  {editingId === policy.id ? (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        className="btn-sm btn-success"
                        disabled={saving}
                        onClick={() => handleSaveEdit(policy.id, false)}
                      >
                        Save
                      </button>
                      <button
                        className="btn-sm btn-primary"
                        disabled={saving}
                        onClick={() => handleSaveEdit(policy.id, true)}
                        title="Save and apply new allocation to all users"
                      >
                        Save + Sync Users
                      </button>
                      <button
                        className="btn-sm btn-outline"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-sm btn-outline"
                      onClick={() => handleEditClick(policy)}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                  No leave policies configured yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: '6px', fontSize: '0.85rem', color: '#0369a1' }}>
        <strong>ℹ️ How it works:</strong>
        <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: 0 }}>
          <li>"Annual Days" = entitled leave days per year for each type.</li>
          <li>"Save + Sync Users" applies the new allocation to ALL staff for the current year.</li>
          <li>"Allow Negative (LOP)" — if checked, users can apply even with zero balance; excess days are salary-deducted. If unchecked, the application is blocked.</li>
          <li>Use "Bulk Allocate Days" for year-start setup across all users at once.</li>
        </ul>
      </div>

      {/* Bulk Allocate Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>Bulk Allocate Leave Days</h2>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleBulkUpdate}>
              <div className="modal-body">
                {error && <div className="error-message">{error}</div>}
                <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  This will create or update leave balance records for ALL eligible staff members.
                </p>

                <div className="form-group">
                  <label>Leave Type *</label>
                  <select
                    value={bulkForm.leave_type}
                    onChange={(e) => setBulkForm({ ...bulkForm, leave_type: e.target.value })}
                    required
                  >
                    {LEAVE_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="number"
                    value={bulkForm.year}
                    onChange={(e) => setBulkForm({ ...bulkForm, year: parseInt(e.target.value) })}
                    min="2020"
                    max="2100"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Allocated Days *</label>
                  <input
                    type="number"
                    value={bulkForm.allocated_days}
                    onChange={(e) => setBulkForm({ ...bulkForm, allocated_days: e.target.value })}
                    min="0"
                    step="0.5"
                    required
                    placeholder="e.g., 10"
                  />
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="reset_used"
                    checked={bulkForm.reset_used}
                    onChange={(e) => setBulkForm({ ...bulkForm, reset_used: e.target.checked })}
                  />
                  <label htmlFor="reset_used" style={{ marginBottom: 0 }}>
                    Reset used days to 0 (use only for new year setup)
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowBulkModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Updating…' : 'Apply to All Users'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeavePolicyManager;